import React, { Component } from 'react';
import PropTypes, { bool } from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import classNames from 'classnames';
import { ResponsiveImage, IconSpinner } from '../../components';
import { propTypes } from '../../util/types';

import defaultListingImg from '../../assets/default-img-listing/default-img-listing.jpg'
import defaultListingImgSmall from '../../assets/default-img-listing/scaled-small.webp';
import defaultListingImgMedium from '../../assets/default-img-listing/scaled-medium.webp';
import defaultListingImgLarge from '../../assets/default-img-listing/scaled-large.webp';
import defaultListingImgXLarge from '../../assets/default-img-listing/scaled-xlarge.webp';

import css from './ImageCarousel.css';

const KEY_CODE_LEFT_ARROW = 37;
const KEY_CODE_RIGHT_ARROW = 39;

class ImageCarousel extends Component {
  constructor(props) {
    super(props);
    this.state = { selectedImageIndex: 0, selectedImageLoaded: false };
    this.onKeyUp = this.onKeyUp.bind(this);
    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
  }
  componentDidMount() {
    window.addEventListener('keyup', this.onKeyUp);
  }
  componentWillUnmount() {
    window.removeEventListener('keyup', this.onKeyUp);
  }
  onKeyUp(e) {
    if (e.keyCode === KEY_CODE_LEFT_ARROW) {
      this.prev();
    } else if (e.keyCode === KEY_CODE_RIGHT_ARROW) {
      this.next();
    }
  }
  prev(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const count = this.props.images.length;
    if (count < 2) {
      return;
    }
    this.setState(prevState => {
      const newIndex = count > 0 ? (count + prevState.selectedImageIndex - 1) % count : 0;
      return { selectedImageIndex: newIndex, selectedImageLoaded: false };
    });
  }
  next(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const count = this.props.images.length;
    if (count < 2) {
      return;
    }
    this.setState(prevState => {
      const newIndex = count > 0 ? (count + prevState.selectedImageIndex + 1) % count : 0;
      return { selectedImageIndex: newIndex, selectedImageLoaded: false };
    });
  }
  render() {
    const { rootClassName, className, images, intl, isSelected } = this.props;
    const classes = classNames(rootClassName || css.root, className);

    const naturalIndex = this.state.selectedImageIndex + 1;
    const imageIndex =
      images.length > 0 ? (
        <span className={css.imageIndex}>
          {naturalIndex}/{images.length}
        </span>
      ) : null;

    const buttonPrevClasses = classNames(css.prev, {
      [css.isFullscreen]: this.props.isFullscreen,
    })
    const buttonNextClasses = classNames(css.next, {
      [css.isFullscreen]: this.props.isFullscreen,
    })

    const prevButton =
      images.length > 1 ? <button className={buttonPrevClasses} onClick={this.prev} /> : null;
    const nextButton =
      images.length > 1 ? <button className={buttonNextClasses} onClick={this.next} /> : null;

    const imageAltText = intl.formatMessage(
      {
        id: 'ImageCarousel.imageAltText',
      },
      {
        index: naturalIndex,
        count: images.length,
      }
    );

    const markImageLoaded = index => () => {
      this.setState(prevState => {
        if (prevState.selectedImageIndex === index) {
          // Only mark the image loaded if the current index hasn't
          // changed, i.e. user hasn't already changed to another
          // image index.
          return { selectedImageLoaded: true };
        }
        return {};
      });
    };

    const currentImageIsLoaded = images.length === 0 || this.state.selectedImageLoaded;
    const loadingIconClasses = classNames(css.loading, {
      [css.loadingVisible]: !currentImageIsLoaded,
    });
    const imageClasses = classNames(css.image, {
      [css.imageLoading]: !currentImageIsLoaded,
      [rootClassName]: rootClassName
    });

    const defaultListingImgVariants = `${defaultListingImgSmall} 320w, ${defaultListingImgMedium} 750w, ${defaultListingImgLarge} 1024w, ${defaultListingImgXLarge} 2400w`;

    return (
      <div className={classes}>
        <div className={classNames(css.imageWrapper, isSelected && css.imageSelected)}>
          <IconSpinner className={loadingIconClasses} />
          { images.length === 0 && !this.props.isOwnListing
            ? <img src={defaultListingImg} alt="defaultImg" className={imageClasses} srcSet={defaultListingImgVariants} sizes="(max-width: 767px) 100vw, 80vw" />
            : <ResponsiveImage
                className={imageClasses}
                alt={imageAltText}
                image={images[this.state.selectedImageIndex]}
                onLoad={markImageLoaded(this.state.selectedImageIndex)}
                onError={markImageLoaded(this.state.selectedImageIndex)}
                variants={['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge', 'landscape-crop', 'landscape-crop2x', 'landscape-crop4x', 'landscape-crop6x']}
                sizes="(max-width: 767px) 100vw, 80vw"
              />
          }
        </div>
        {imageIndex}
        {prevButton}
        {nextButton}
      </div>
    );
  }
}

ImageCarousel.defaultProps = {
  rootClassName: null,
  className: null,
  isFullscreen: true
};

const { string, arrayOf } = PropTypes;

ImageCarousel.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  isOwnListing: bool,
  isFullscreen: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ImageCarousel);
